import { RadioGroup, RadioGroupIndicator, RadioGroupItem } from "@radix-ui/react-radio-group";
import { cva } from "class-variance-authority";
import React from "react";

// types
import { Option } from "../../../types/Option";

// utils
import { cn } from "../../../lib/utils";

// components
import { Text } from "../../atoms/Text";

const RadioGroupVariants = cva("flex flex-col gap-2", {
	variants: {
		orientation: {
			vertical: "flex-col",
			horizontal: "flex-row",
		},
	},
});

const RadioGroupItemVariants = cva(
	"flex items-center justify-center rounded-full border border-solid border-neutral-3 bg-transparent checked:border-green-5 checked:ring-primary-5 focus:border-primary-5 focus:ring-primary-5 data-[state=checked]:border-primary-5 data-[state=checked]:bg-primary-5",
	{
		variants: {
			orientation: {
				vertical: "flex-col",
				horizontal: "flex-row",
			},
			size: {
				small: "size-4",
				large: "size-6",
			},
			error: {
				true: "border-errored focus:border-errored focus:ring-errored",
			},
			disabled: {
				true: "cursor-not-allowed opacity-disabled",
				false: "active:border-neutral-` cursor-pointer hover:border-neutral-5",
			},
		},
		defaultVariants: {
			size: "small",
			error: false,
			disabled: false,
			orientation: "vertical",
		},
	}
);

export type RadioButtonsProps<T> = {
	/** Array of options to display as radio buttons */
	options: Option<T>[];
	/** Currently selected value */
	value: T;
	/** Callback function called when selection changes */
	onChange: (value: T) => void;
	/** Whether the radio buttons are disabled */
	disabled?: boolean;
	/** Whether the radio buttons are errored */
	errored?: boolean;
	/** Additional className to apply to the radio group */
	dangerouslySetClassName?: string;
	/**
	 * Layout orientation of the radio buttons
	 * @default "vertical"
	 */
	orientation?: "vertical" | "horizontal";
	/**
	 * Unique identifier for the radio group
	 * @default "radio-buttons"
	 */
	id: string;
	/** Size of the radio buttons */
	size?: "small" | "large";
	/** Test ID for the radio buttons */
	"data-testid"?: string;
};

/**
 * RadioButtons Group Component, used for single selection from a list of options
 * This component is used outside of a form context. If you need a form-connected radio buttons group, use FormRadioButtons
 */
const RadioButtons = <T extends string>({
	options,
	value,
	onChange,
	disabled,
	errored,
	dangerouslySetClassName,
	size = "small",
	orientation,
	id,
	"data-testid": dataTestId,
}: RadioButtonsProps<T>) => {
	const handleChange = (newValue: string) => {
		onChange(newValue as T);
	};

	return (
		<RadioGroup
			id={id}
			data-testid={dataTestId}
			className={cn(RadioGroupVariants({ orientation }), dangerouslySetClassName)}
			onValueChange={handleChange}
			value={value?.toString() || ""}
			orientation={orientation}
			disabled={disabled}
			aria-invalid={errored}
		>
			{options.map((option) => (
				<div className="flex items-start gap-2" key={option.value}>
					<RadioGroupItem
						id={`${id}-${option.value}`}
						key={option.value}
						checked={option.value === value}
						value={option.value}
						disabled={option.disabled || disabled}
						className={cn(
							RadioGroupItemVariants({ orientation, size, disabled: option.disabled || disabled })
						)}
					>
						<RadioGroupIndicator className="absolute block size-2 rounded-full bg-neutral-0" />
					</RadioGroupItem>
					<label
						htmlFor={`${id}-${option.value}`}
						className={cn(
							disabled || option.disabled ? "cursor-not-allowed opacity-[38%]" : "cursor-pointer"
						)}
					>
						<Text size={"paragraph-3"}>{option.label}</Text>
						{option.subtext && (
							<Text id={`${id}-${option.value}-description`} size={"caption"} color="neutral-4">
								{option.subtext}
							</Text>
						)}
					</label>
				</div>
			))}
		</RadioGroup>
	);
};

RadioButtons.displayName = "RadioButtons";

export { RadioButtons };
